import { useState } from 'react';
import { Link } from 'react-router';
import {
  CommandValidationError,
  RECEIVER_RELATIONSHIPS,
  stopBarcodes,
  buildArrivePayload,
  buildDeliverPayload,
  checkStopBarcode,
  deliveryProblems,
  deliveryWarnings,
  formatAddress,
  type OpsRun,
  type OpsShipment,
  type StopView,
} from '@ecobox/firebase';
import { isCurrencyCode } from '@ecobox/domain';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  MoneyInput,
  ScanInput,
  ScannerView,
  Select,
  SignaturePad,
  scanFeedback,
  useToast,
  type ScanVerdict,
} from '@ecobox/ui';
import { PhotoField } from '../../components/PhotoField';
import { useGeo } from '../../hooks/useGeo';
import { loadBarcodeDetector } from '../../scanner/detector';
import { dataUrlToBlob } from '../../session/files';
import { useServices } from '../../services';
import { BackLink, ContactLinks, Problems, fmt, stopStatus } from './shared';
import { FailForm } from './FailForm';

interface Last {
  barcode: string;
  tone: 'ok' | 'bad';
  message: string;
}

/** One delivery stop (05 §5.2): the boxes, the proof, the money. Works with no signal. */
export function DeliveryStop({
  run,
  stop,
  shipment,
  problems,
}: {
  run: OpsRun & { waiting: boolean };
  stop: StopView;
  shipment: OpsShipment | undefined;
  problems: React.ComponentProps<typeof Problems>['problems'];
}) {
  const { writer, uploads } = useServices();
  const toast = useToast();
  const { geo, state: geoState, fix } = useGeo(true);

  const [mode, setMode] = useState<'deliver' | 'fail'>('deliver');
  const [barcodes, setBarcodes] = useState<string[]>([]);
  const [last, setLast] = useState<Last | null>(null);
  const [camera, setCamera] = useState(false);
  const [hasCode, setHasCode] = useState(true);
  const [code, setCode] = useState('');
  const [idPhotos, setIdPhotos] = useState<File[]>([]);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [signature, setSignature] = useState<string | null>(null);
  const [collected, setCollected] = useState<number | undefined>(stop.collectMinor);
  const [method, setMethod] = useState<'cash' | 'transfer'>('cash');
  const [notify, setNotify] = useState(false);
  const [showProblems, setShowProblems] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const planned = stopBarcodes(stop);
  const consigneeName = stop.contact?.name ?? shipment?.consignee.name ?? stop.label;
  const address = stop.address ?? shipment?.consignee.address;
  const phones = stop.contact?.phones.length
    ? stop.contact.phones
    : (shipment?.consignee.phones ?? []);
  const status = stopStatus(stop.status, stop.waiting);
  const active = run.status === 'active';
  const finished = stop.status === 'done' || stop.status === 'failed' || stop.status === 'skipped';
  const currency = isCurrencyCode(run.currency) ? run.currency : 'AED';

  const draft = {
    barcodes,
    receiverName: name,
    relationship,
    hasCode,
    code,
    idPhotoCount: idPhotos.length,
    photoCount: photos.length,
    signed: signature !== null,
    collectedMinor: collected,
  };
  const found = deliveryProblems(stop, draft);
  const warnings = deliveryWarnings(stop, draft);

  const handleScan = (raw: string): ScanVerdict => {
    const checked = checkStopBarcode(stop, barcodes, raw);
    if (!checked.ok) {
      setLast({ barcode: checked.barcode, tone: 'bad', message: checked.reason });
      return 'bad';
    }
    setBarcodes((b) => [...b, checked.barcode]);
    setLast({ barcode: checked.barcode, tone: 'ok', message: 'Scanned' });
    return 'ok';
  };

  const arrive = async () => {
    setBusy(true);
    try {
      const position = await fix();
      writer.send(
        'arriveStop',
        buildArrivePayload({
          runId: run.id,
          stopId: stop.id,
          at: new Date().toISOString(),
          geo: position,
          notifyCustomer: notify,
        }),
      );
    } catch (err) {
      toast.show({
        title: 'Not saved',
        message: err instanceof Error ? err.message : String(err),
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  };

  const deliver = async () => {
    setShowProblems(true);
    setError(null);
    if (found.length > 0) return;
    setBusy(true);
    try {
      const position = await Promise.race([
        fix(),
        new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 3000)),
      ]);
      // Pictures are shrunk and saved on the phone first and upload when there is signal; the
      // command names where they will be, so the delivery does not wait for the network.
      const idPhotoPath =
        !hasCode && idPhotos[0]
          ? await uploads.enqueueUpload(idPhotos[0], { kind: 'pod' })
          : undefined;
      const photoPaths: string[] = [];
      for (const photo of photos)
        photoPaths.push(await uploads.enqueueUpload(photo, { kind: 'pod' }));
      const signaturePath = signature
        ? await uploads.enqueueUpload(dataUrlToBlob(signature), { kind: 'signature' })
        : undefined;
      writer.send(
        'deliverStop',
        buildDeliverPayload({
          runId: run.id,
          stopId: stop.id,
          draft,
          photoPaths,
          idPhotoPath,
          signaturePath,
          geo: position,
          method,
        }),
      );
      toast.show({
        title: 'Delivered',
        message: 'Saved. It goes to the server when you have signal.',
        tone: 'success',
      });
    } catch (err) {
      setError(
        err instanceof CommandValidationError && err.issues.length > 0
          ? err.issues.join('; ')
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <>
      <BackLink to={`/run/${run.id}`}>The run</BackLink>
      <div>
        <h1 className="ops-title">{consigneeName}</h1>
        <p className="ops-muted" style={{ margin: 0 }}>
          Stop {stop.sequence + 1} · delivery <Badge tone={status.tone}>{status.label}</Badge>
        </p>
      </div>
    </>
  );

  if (finished) {
    const o = stop.outcome;
    return (
      <div className="ops-stack">
        {header}
        <Problems problems={problems} stopId={stop.id} />
        <Card title={stop.status === 'done' ? 'Delivered' : 'Not delivered'}>
          <div className="ops-stack">
            {stop.status === 'done' ? (
              <>
                <p style={{ margin: 0 }}>
                  Received by <strong>{o?.receiverName || 'the receiver'}</strong>
                  {o?.relationship ? ` (${o.relationship})` : ''}.{' '}
                  {o?.deliveredBarcodes.length ?? 0} box
                  {(o?.deliveredBarcodes.length ?? 0) === 1 ? '' : 'es'}.
                </p>
                {(o?.collectedMinor ?? 0) > 0 ? (
                  <p style={{ margin: 0 }}>
                    Collected {fmt(o?.collectedMinor ?? 0, run.currency)}.
                  </p>
                ) : null}
              </>
            ) : (
              <p style={{ margin: 0 }}>
                {o?.reason ? `Reason: ${o.reason.replace(/_/g, ' ')}.` : 'Marked as failed.'}{' '}
                {o?.note ?? ''}
              </p>
            )}
            {stop.waiting ? (
              <p className="ops-muted" style={{ margin: 0 }}>
                Saved on this phone. It goes to the server when you have signal.
              </p>
            ) : null}
            <Link to={`/run/${run.id}`}>
              <Button size="touch" variant="cta" block>
                Back to the run
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (mode === 'fail') {
    return (
      <div className="ops-stack">
        {header}
        <FailForm
          runId={run.id}
          stopId={stop.id}
          type="failStop"
          onCancel={() => setMode('deliver')}
        />
      </div>
    );
  }

  return (
    <div className="ops-stack">
      {header}
      <Problems problems={problems} stopId={stop.id} />

      {!active ? (
        <p role="alert" className="ops-error">
          The run has not started. Start it from the run page before you deliver.
        </p>
      ) : null}

      <Card title="Where" data-tour="stop-arrive">
        <div className="ops-stack">
          <div>{formatAddress(address) || stop.label}</div>
          {address?.landmark ? <div className="ops-muted">Near {address.landmark}</div> : null}
          <ContactLinks geo={stop.geo} address={address} phones={phones} label={stop.label} />
          {stop.collectMinor > 0 ? (
            <p style={{ margin: 0 }}>
              Collect <span className="ops-money">{fmt(stop.collectMinor, run.currency)}</span>{' '}
              (destination charges)
            </p>
          ) : null}
          {stop.status === 'pending' ? (
            <>
              <Checkbox
                label="Tell the customer I am arriving soon"
                checked={notify}
                onChange={setNotify}
              />
              <Button
                size="touch"
                variant="secondary"
                block
                loading={busy}
                disabled={!active}
                onClick={() => void arrive()}
              >
                I have arrived
              </Button>
            </>
          ) : (
            <p className="ops-ok" style={{ margin: 0 }}>
              Arrived{stop.waiting ? ' (waiting to sync)' : ''}.
            </p>
          )}
        </div>
      </Card>

      <Card title={`Boxes · ${barcodes.length} of ${planned.length}`} data-tour="stop-boxes">
        <div className="ops-stack">
          <ScanInput
            autoFocus={false}
            keepFocus={false}
            onScan={(c) => scanFeedback(handleScan(c))}
            label="Scan or type a box barcode"
            status={last ? (last.tone === 'bad' ? 'error' : 'ok') : 'idle'}
          />
          {last ? (
            <div className="ops-last" data-tone={last.tone} role="status">
              <span className="ops-code">{last.barcode}</span> {last.message}
            </div>
          ) : null}
          <Button size="touch" variant="cta" block onClick={() => setCamera(true)}>
            Scan with the camera
          </Button>
          <ul className="ops-list" aria-label="Boxes for this stop">
            {planned.map((b) => {
              const done = barcodes.includes(b);
              return (
                <li key={b} className="ops-row" data-tone={done ? 'ok' : undefined}>
                  <div className="ops-row__top">
                    <span className="ops-code">{b}</span>
                    {done ? (
                      <Button
                        size="lg"
                        variant="ghost"
                        onClick={() => setBarcodes((list) => list.filter((x) => x !== b))}
                      >
                        Remove
                      </Button>
                    ) : (
                      <Badge tone="neutral">To scan</Badge>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </Card>

      <Card title="Is it the right person?" data-tour="stop-code">
        <div className="ops-stack">
          <Checkbox
            label="The receiver has no code"
            hint="Then take a photo of their ID."
            checked={!hasCode}
            onChange={(v) => setHasCode(!v)}
          />
          {hasCode ? (
            <Field label="4-digit delivery code" required>
              {(p) => (
                <Input
                  {...p}
                  size="touch"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={4}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              )}
            </Field>
          ) : (
            <PhotoField
              label="Photo of the receiver’s ID"
              files={idPhotos}
              onChange={setIdPhotos}
              max={1}
            />
          )}
        </div>
      </Card>

      <Card title="Who received it" data-tour="stop-receiver">
        <div className="ops-stack">
          {consigneeName !== stop.label || shipment ? (
            <Button
              size="lg"
              variant="secondary"
              onClick={() => {
                setName(consigneeName);
                setRelationship(RECEIVER_RELATIONSHIPS[0]);
              }}
            >
              It is {consigneeName}
            </Button>
          ) : null}
          <Field label="Receiver’s name" required>
            {(p) => (
              <Input
                {...p}
                size="touch"
                maxLength={80}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <Field label="Relationship to the consignee">
            {(p) => (
              <Select
                {...p}
                size="touch"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
              >
                <option value="">Choose…</option>
                {RECEIVER_RELATIONSHIPS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <PhotoField
            label="Photos of the delivered boxes"
            files={photos}
            onChange={setPhotos}
            max={10}
          />
          <SignaturePad label="Receiver’s signature" value={signature} onChange={setSignature} />
        </div>
      </Card>

      {stop.collectMinor > 0 || (collected ?? 0) > 0 ? (
        <Card title="Money" data-tour="stop-money">
          <div className="ops-stack">
            <Field label="Amount collected" required>
              {(p) => (
                <MoneyInput
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  size="lg"
                  currency={currency}
                  value={collected}
                  onChange={setCollected}
                />
              )}
            </Field>
            <Field label="Paid by">
              {(p) => (
                <Select
                  {...p}
                  size="touch"
                  value={method}
                  onChange={(e) => setMethod(e.target.value === 'transfer' ? 'transfer' : 'cash')}
                >
                  <option value="cash">Cash</option>
                  <option value="transfer">Bank transfer</option>
                </Select>
              )}
            </Field>
          </div>
        </Card>
      ) : null}

      <p className="ops-muted" style={{ margin: 0 }}>
        {geo
          ? `Location noted (${geo.lat.toFixed(4)}, ${geo.lng.toFixed(4)}).`
          : geoState === 'asking'
            ? 'Looking for your location…'
            : 'Location is not available. You can still deliver.'}
      </p>

      {warnings.map((w) => (
        <p key={w} className="ops-muted" style={{ margin: 0 }} role="status">
          {w}
        </p>
      ))}
      {showProblems && found.length > 0 ? (
        <ul role="alert" className="ops-error" style={{ margin: 0, paddingLeft: 18 }}>
          {found.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <p role="alert" className="ops-error">
          {error}
        </p>
      ) : null}

      <div className="ops-actionbar" data-tour="stop-actions">
        <Button
          size="touch"
          variant="cta"
          block
          loading={busy}
          disabled={!active}
          onClick={() => void deliver()}
        >
          Delivered
        </Button>
        <Button size="lg" variant="ghost" block onClick={() => setMode('fail')}>
          I could not deliver
        </Button>
      </div>

      {camera ? (
        <ScannerView
          loadDetector={loadBarcodeDetector}
          title={`Delivery · ${consigneeName}`}
          count={barcodes.length}
          countLabel={`of ${planned.length}`}
          lastResult={
            last
              ? {
                  code: last.barcode,
                  tone: last.tone === 'ok' ? 'ok' : 'bad',
                  message: last.message,
                }
              : null
          }
          onRead={handleScan}
          onClose={() => setCamera(false)}
        />
      ) : null}
    </div>
  );
}
